import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private currentSnackBar: MatSnackBarRef<any> | null = null;

  constructor(private snackBar: MatSnackBar) {}

  private buildMessage(title: string, msg: string): string {
    const cleanTitle = title?.trim();
    const cleanMessage = msg?.trim();

    if (!cleanTitle) {
      return cleanMessage;
    }

    if (!cleanMessage) {
      return cleanTitle;
    }

    return `${cleanTitle}\n${cleanMessage}`;
  }

  // Generic method to show snack bar
  private showMessage(
    message: string,
    panelClass: string[],
    action: string = '',
    duration: number = 3200,
    politeness: 'polite' | 'assertive' = 'polite'
  ): MatSnackBarRef<any> {
    // Dismiss any existing snackbar first
    if (this.currentSnackBar) {
      this.currentSnackBar.dismiss();
    }

    const config: MatSnackBarConfig = {
      duration: duration,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: panelClass,
      politeness,
      announcementMessage: message,
    };

    this.currentSnackBar = this.snackBar.open(message, action || undefined, config);

    // Clear the reference when the snackbar is dismissed
    this.currentSnackBar.afterDismissed().subscribe(() => {
      this.currentSnackBar = null;
    });

    // Auto-dismiss on action click
    this.currentSnackBar.onAction().subscribe(() => {
      this.currentSnackBar?.dismiss();
    });

    return this.currentSnackBar;
  }

  // Success alert
  success(
    title: string,
    msg: string,
    action: string = '',
    duration: number = 2800
  ): MatSnackBarRef<any> {
    return this.showMessage(
      this.buildMessage(title, msg),
      ['app-snackbar', 'app-snackbar--success'],
      action,
      duration
    );
  }

  // Error alert
  error(
    title: string,
    msg: string,
    action: string = 'OK',
    duration: number = 4500
  ): MatSnackBarRef<any> {
    return this.showMessage(
      this.buildMessage(title, msg),
      ['app-snackbar', 'app-snackbar--error'],
      action,
      duration,
      'assertive'
    );
  }

  // Info alert
  info(
    title: string,
    msg: string,
    action: string = '',
    duration: number = 3400
  ): MatSnackBarRef<any> {
    return this.showMessage(
      this.buildMessage(title, msg),
      ['app-snackbar', 'app-snackbar--info'],
      action,
      duration
    );
  }

  // Warning alert
  warning(
    title: string,
    msg: string,
    action: string = 'OK',
    duration: number = 4200
  ): MatSnackBarRef<any> {
    return this.showMessage(
      this.buildMessage(title, msg),
      ['app-snackbar', 'app-snackbar--warning'],
      action,
      duration,
      'assertive'
    );
  }

  // Method to manually dismiss current snackbar
  dismiss(): void {
    if (this.currentSnackBar) {
      this.currentSnackBar.dismiss();
    }
  }

  // Method to dismiss all snackbars
  dismissAll(): void {
    this.snackBar.dismiss();
  }
}
